import { InlineCodeHighlight } from '@mantine/code-highlight';
import { Anchor, Alert, Button, CloseButton, Code, NumberInput, Popover, Tooltip } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useCallback, useMemo, useState } from 'react';
import { useTestVariables } from '~/hooks/test/useTestVariables';
import { ParameterizedInput } from '../../ParameterizedInput';
import type { Part, ValuePart } from '../converters';
import type { ValueType } from '~/server/logic/steps/operation/types';
import { zSwipe } from '~/common/recorder/recorder-operation';
import type { OperationParentContext } from '~/server/logic/steps/operation/types';
import { isLocalhostLike, LOCALHOST_TUNNEL_DOC } from '~/utils/url';

// Note: We store timeouts as ms but display them as secs
export default function ValuePart({
  testContentId,
  operationId,
  part: { value, valueType },
  onClick,
  toggleEditing,
  readonly,
  updateFunc,
  parts,
  parentContexts,
}: {
  testContentId: string;
  operationId: string;
  part: ValuePart;
  onClick?: () => void;
  // For Operation wide editing
  toggleEditing: (editing: boolean) => void;
  readonly?: boolean;
  updateFunc: (part: Omit<ValuePart, 'type'>) => Promise<void>;
  parts: Part[];
  parentContexts?: OperationParentContext[];
}) {
  const [editing, setEditing] = useState(false);
  const testVariables = useTestVariables({
    testContentId,
    limit: { type: 'operationId', id: operationId },
    parentContexts,
  });

  const form = useForm<{
    value: string;
    valueType: ValueType;
  }>({
    initialValues: {
      value: value || '',
      valueType: valueType || 'STATIC',
    },
  });
  const handleFormSubmission = useCallback(
    async ({ value, valueType }: { value: string; valueType: ValueType }) => {
      const response = { value, valueType };
      // Explicitly set instead of toggling to avoid state drift
      toggleEditing(false);
      setEditing(false);
      await updateFunc(response);
    },
    [updateFunc, editing],
  );

  // Filter through parts to see if any special displaying is needed
  const actionType = useMemo(() => {
    const hasTimeout = () => parts.some((part) => part.type === 'action' && part.action?.includes('Timeout'));
    const hasNavigation = () =>
      parts.some((part) => part.type === 'action' && (part.action === 'navigate' || part.action === 'goto'));
    const hasSwipe = () => parts.some((part) => part.type === 'action' && part.action === 'swipe');
    const hasScroll = () => parts.some((part) => part.type === 'action' && part.action === 'scroll');
    return (
      hasTimeout() ? 'timeout'
      : hasNavigation() ? 'navigate'
      : hasSwipe() ? 'swipe'
      : hasScroll() ? 'scroll'
      : 'misc'
    );
  }, [parts]);

  const swipeValue = actionType === 'swipe' ? zSwipe.parse(JSON.parse(value)) : undefined;
  const localhostBlocked =
    actionType === 'navigate' && form.values.valueType === 'STATIC' && isLocalhostLike(form.values.value || '');

  return (
    <div className="flex gap-2">
      {actionType === 'misc' && <div>with</div>}
      <Popover
        width={365}
        position="bottom"
        withArrow
        shadow="md"
        opened={editing}
        closeOnClickOutside
        onClose={() => {
          toggleEditing(false);
          setEditing(false);
        }}
        onDismiss={() => {
          toggleEditing(false);
          setEditing(false);
        }}
      >
        <Popover.Target>
          <Tooltip
            label="Double click to edit"
            openDelay={500}
            withArrow
            disabled={readonly || editing}
          >
            <div
              className="break-all rounded-md"
              onDoubleClick={() => {
                onClick?.();
                toggleEditing(true);
                setEditing(true);
              }}
            >
              {valueType === 'STATIC' ?
                <span className="text-yellow-900 hover:cursor-pointer dark:text-yellow-600">
                  {actionType === 'timeout' ?
                    `${Number(value) / 1000}`
                  : actionType === 'swipe' && swipeValue ?
                    `${swipeValue.direction}wards`
                  : value}
                </span>
              : <InlineCodeHighlight
                  code={value}
                  language="tsx"
                />
              }
            </div>
          </Tooltip>
        </Popover.Target>
        <Popover.Dropdown
          className="dark:bg-neutral-800"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col">
            <div className="flex justify-end">
              <CloseButton
                variant="transparent"
                size="md"
                onClick={() => {
                  toggleEditing(false);
                  setEditing(false);
                }}
              />
            </div>
            <form
              className="mb-2 flex flex-col gap-2"
              onSubmit={form.onSubmit(handleFormSubmission)}
            >
              {actionType === 'timeout' ?
                <NumberInput
                  allowNegative={false}
                  label="Value"
                  autoFocus
                  disabled={readonly}
                  defaultValue={Number(form.values.value) / 1000}
                  onChange={(value) => {
                    form.setFieldValue('value', (Number(value) * 1000).toString());
                  }}
                />
              : <ParameterizedInput
                  label="Value"
                  autoFocus
                  disabled={readonly}
                  value={form.values.value || ''}
                  valueType={form.values.valueType || 'STATIC'}
                  onValueChange={(x) => form.setFieldValue('value', x)}
                  onValueTypeChange={(x) => form.setFieldValue('valueType', x)}
                  variables={testVariables}
                />
              }

              {!!localhostBlocked && (
                <Alert
                  color="yellow"
                  radius="md"
                >
                  To test local apps, install the <Code>Stably CLI</Code> and run <Code>stably dev</Code> to create a
                  secure tunnel.{' '}
                  <Anchor
                    href={LOCALHOST_TUNNEL_DOC}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Learn more
                  </Anchor>
                  .
                </Alert>
              )}

              {!readonly && (
                <div className="flex justify-end space-x-1 py-4">
                  <Button
                    type="submit"
                    disabled={readonly || localhostBlocked}
                  >
                    Save
                  </Button>
                </div>
              )}
            </form>
          </div>
        </Popover.Dropdown>
        {actionType === 'timeout' ?
          <div>seconds</div>
        : actionType === 'scroll' ?
          <span>&nbsp;pixels on</span>
        : undefined}
      </Popover>
    </div>
  );
}
